import { useSelector } from "react-redux";
import { RootState } from "../../../store";
import { formMappings } from "../../../config/formMappings";

// State name mapping: abbreviation to full name
const STATE_ABBREVIATIONS: { [key: string]: string } = {
  AL: "Alabama",
  AK: "Alaska",
  AZ: "Arizona",
  AR: "Arkansas",
  CA: "California",
  CO: "Colorado",
  CT: "Connecticut",
  DE: "Delaware",
  FL: "Florida",
  GA: "Georgia",
  HI: "Hawaii",
  ID: "Idaho",
  IL: "Illinois",
  IN: "Indiana",
  IA: "Iowa",
  KS: "Kansas",
  KY: "Kentucky",
  LA: "Louisiana",
  ME: "Maine",
  MD: "Maryland",
  MA: "Massachusetts",
  MI: "Michigan",
  MN: "Minnesota",
  MS: "Mississippi",
  MO: "Missouri",
  MT: "Montana",
  NE: "Nebraska",
  NV: "Nevada",
  NH: "New Hampshire",
  NJ: "New Jersey",
  NM: "New Mexico",
  NY: "New York",
  NC: "North Carolina",
  ND: "North Dakota",
  OH: "Ohio",
  OK: "Oklahoma",
  OR: "Oregon",
  PA: "Pennsylvania",
  RI: "Rhode Island",
  SC: "South Carolina",
  SD: "South Dakota",
  TN: "Tennessee",
  TX: "Texas",
  UT: "Utah",
  VT: "Vermont",
  VA: "Virginia",
  WA: "Washington",
  WV: "West Virginia",
  WI: "Wisconsin",
  WY: "Wyoming",
  DC: "District of Columbia",
  // Canadian provinces and territories
  AB: "Alberta",
  BC: "British Columbia",
  MB: "Manitoba",
  NB: "New Brunswick",
  NL: "Newfoundland and Labrador",
  NS: "Nova Scotia",
  NT: "Northwest Territories",
  NU: "Nunavut",
  ON: "Ontario",
  PE: "Prince Edward Island",
  QC: "Quebec",
  SK: "Saskatchewan",
  YT: "Yukon",
};

// Reverse mapping: full name to abbreviation
const STATE_FULL_NAMES: { [key: string]: string } = Object.fromEntries(
  Object.entries(STATE_ABBREVIATIONS).map(([abbr, full]) => [full, abbr])
);

/**
 * Normalizes state names to a consistent format (prefers full name)
 * Handles both abbreviations (CA) and full names (California)
 */
function normalizeStateName(state: string): string {
  if (!state || typeof state !== "string") return state;

  const trimmed = state.trim();

  // If it's a 2-letter abbreviation, convert to full name
  if (trimmed.length === 2 && STATE_ABBREVIATIONS[trimmed.toUpperCase()]) {
    return STATE_ABBREVIATIONS[trimmed.toUpperCase()];
  }

  // If it's a full name, check if we have it in our mapping
  const upperKey = Object.keys(STATE_FULL_NAMES).find(
    (key) => key.toLowerCase() === trimmed.toLowerCase()
  );
  if (upperKey) {
    return upperKey; // Return the properly capitalized version
  }

  // If not found in mapping, return as-is (could be a province or other location)
  return trimmed;
}

export default function LocationDistributionTable() {
  const entries = useSelector((state: RootState) => state.formEntries.entries);

  const activeFormName = useSelector(
    (state: RootState) => state.formEntries.activeFormName || ""
  );

  const formMapping = formMappings[activeFormName as keyof typeof formMappings];
  const version = formMapping?.version || "1";
  const addressObjectKey = (formMapping?.fieldMappings as any)?.address_object;
  const stateFieldKey = (formMapping?.fieldMappings as any)?.state;
  const numberOfRegistrantsFieldKey = (formMapping?.fieldMappings as any)?.[
    "Number of Registrants"
  ];

  // Calculate location distribution
  const locationDistribution = entries.reduce((acc, entry) => {
    let state: string | null = null;

    if (version === "1") {
      // Version 1: Get state from address_object
      if (addressObjectKey && entry.meta[addressObjectKey]) {
        const addressObject = entry.meta[addressObjectKey] as any;
        // The state field key within address_object is "state" according to formMappings
        if (
          addressObject &&
          typeof addressObject === "object" &&
          addressObject.state
        ) {
          state = addressObject.state;
        }
      }
    } else {
      // Version 2+: Get state directly from mapped field
      if (stateFieldKey && entry.meta[stateFieldKey]) {
        const stateValue = entry.meta[stateFieldKey];
        // Handle both string and array cases
        if (Array.isArray(stateValue)) {
          state = stateValue[0]; // Take first value if array
        } else if (typeof stateValue === "string") {
          state = stateValue;
        } else {
          state = String(stateValue);
        }
      }
    }

    // Get the number of registrants for this entry
    const numberOfRegistrants = parseInt(
      entry.meta[numberOfRegistrantsFieldKey || ""] ||
        entry.meta["Number of Registrants"] ||
        "1",
      10
    );
    const count = isNaN(numberOfRegistrants) ? 1 : numberOfRegistrants;

    // Normalize and count the state by number of registrants
    if (state) {
      const normalizedState = normalizeStateName(state);
      if (normalizedState) {
        acc[normalizedState] = (acc[normalizedState] || 0) + count;
      }
    }

    return acc;
  }, {} as { [location: string]: number });

  const locationsData = Object.entries(locationDistribution)
    .map(([location, count]) => ({
      name: location,
      value: count,
    }))
    .sort((a, b) => b.value - a.value); // Sort by count descending

  return (
    <div className="overflow-y-scroll no-scrollbar rounded-lg bg-white shadow">
      <table className="min-w-full divide-y divide-gray-300">
        <thead className="bg-gray-50">
          <tr>
            <th
              scope="col"
              className="py-3.5 pl-4 pr-3 text-left text-sm font-semibold text-gray-900 sm:pl-6"
            >
              Location (state/province)
            </th>
            <th
              scope="col"
              className="px-3 py-3.5 text-left text-sm font-semibold text-gray-900"
            >
              Count
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-gray-200 bg-white">
          {locationsData.length > 0 ? (
            locationsData.map((locationData, idx) => (
              <tr key={idx}>
                <td className="whitespace-nowrap py-4 pl-4 pr-3 text-sm font-medium text-gray-900 sm:pl-6">
                  {locationData.name}
                </td>
                <td className="whitespace-nowrap px-3 py-4 text-sm text-gray-500">
                  {locationData.value}
                </td>
              </tr>
            ))
          ) : (
            <tr>
              <td
                colSpan={2}
                className="py-4 pl-4 pr-3 text-sm text-gray-500 text-center sm:pl-6"
              >
                No location data available
              </td>
            </tr>
          )}
        </tbody>
      </table>
    </div>
  );
}
